import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, forkJoin, map } from 'rxjs';
import * as _ from 'lodash-es';
import { environment } from '../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  baseURLs = {
    api: '/gtw',
    custom: '/custom',
    admin: '/admin',
    efile: '/gtw-efile-api',
  };
  options = { withCredentials: true };

  USER_MENU: any;
  USER_MENU_V1: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  ROUTES: any;
  TAX_YEARS: any;

  startUPCompleted = new BehaviorSubject<any>(0);
  navItem$ = this.startUPCompleted.asObservable();

  constructor(private httpClient: HttpClient) {}

  callApis(): Promise<any> {
    return firstValueFrom(
      forkJoin({
        SERVER_CONFIG: this.httpClient
          .get(environment.base_url + '/ajaxGetConfigSettings')
          .pipe(map((data: any) => data)),
        ROUTES: this.httpClient
          .get(environment.base_url + '/getRoutes')
          .pipe(map((data: any) => data)),
        USER_MENU: this.httpClient
          .get(environment.base_url + '/v2/getUserNavigator')
          .pipe(map((data: any) => data)),
        USER_MENU_V1: this.httpClient
          .get(environment.base_url + '/getUserNavigator')
          .pipe(map((data: any) => data)),
        TAX_YEARS: this.httpClient
          .get(environment.base_url + '/getTaxYears', this.options)
          .pipe(map((data: any) => data.callSuccess)),
        USER_SETTINGS: this.httpClient
          .get(environment.base_url + '/getUserSettings')
          .pipe(map((data: any) => data)),
      })
    )
      .then((response: any) => {
        this.SERVER_CONFIG = response.SERVER_CONFIG;
        this.ROUTES = response.ROUTES;
        this.USER_MENU = response.USER_MENU;
        this.USER_MENU_V1 = response.USER_MENU_V1;
        this.USER_SETTINGS = response.USER_SETTINGS;
        sessionStorage.setItem(
          'client_key',
          this.USER_SETTINGS.user.client_key
        );

        if (response.TAX_YEARS) {
          this.TAX_YEARS = this.processTaxYears(response.TAX_YEARS);
        }

        if (this.USER_SETTINGS.user?.selected_theme) {
          this.applyTheme(this.USER_SETTINGS.user.selected_theme);
        }

        this.startUPCompleted.next(1);
        return response;
      })
      .catch((error: any) => {
        console.error('Startup API error:', error);
        throw error;
      });
  }

  private processTaxYears(taxYearData: any): any {
    return _.mapValues(taxYearData, (item: any) => {
      const tempScenarios: any[] = [];
      const tempTaxYears: any[] = [];
      let runxYear: any;

      _.each(item, (o: any) => {
        runxYear = o.tax_year;
        const existingTaxYear: any = _.filter(tempTaxYears, {
          label: o.tax_year,
        });

        if (!existingTaxYear || existingTaxYear.length <= 0) {
          const tempScenario = {
            scenario: o.scenario,
            scenario_desc: o.scenario_desc,
            default_scenario: o.default_scenario,
            is_locked: o.is_locked,
            jcd_key: o.jcd_key,
            scenario_type_code: o.scenario_type_code,
            channel_key: o.channel_key,
            is_issue_key_enabled: o.is_issue_key_enabled,
          };
          tempScenarios.push(tempScenario);

          if (o.default_scenario === 'Y') {
            tempTaxYears.push({
              label: o.tax_year,
              value: o.tax_year,
              scenario: o.scenario,
              scenario_desc: o.scenario_desc,
              scenarios: tempScenarios,
              scenario_type_code: o.scenario_type_code,
              channel_key: o.channel_key,
              is_issue_key_enabled: o.is_issue_key_enabled,
            });
          }
        } else {
          if (o.default_scenario === 'Y') {
            existingTaxYear.scenario = o.scenario;
            existingTaxYear.scenario_desc = o.scenario_desc;
          }
          const existing = _.filter(tempScenarios, { scenario: o.scenario });
          if (!existing || existing.length <= 0) {
            tempScenarios.push({
              scenario: o.scenario,
              scenario_desc: o.scenario_desc,
              default_scenario: o.default_scenario,
              is_locked: o.is_locked,
              jcd_key: o.jcd_key,
              scenario_type_code: o.scenario_type_code,
              channel_key: o.channel_key,
              is_issue_key_enabled: o.is_issue_key_enabled,
            });
          }
        }
      });

      if (tempTaxYears.length > 0) {
        return tempTaxYears;
      }
      return true;
    });
  }

  applyTheme(selectedTheme: string): void {
    const isDark = selectedTheme !== 'theme-white';
    const existingAppkit = document.getElementById('appkit') as HTMLLinkElement;

    // Global theme hook: lets the global dark-override stylesheet (and any
    // `body.theme-black` scoped rules) repaint the legacy gizmoGrid chrome and
    // content that AppKit alone does not cover.
    document.body.classList.toggle('theme-black', isDark);
    document.body.classList.toggle('theme-white', !isDark);

    if (isDark) {
      const appkitHref = 'appkit-dark.css';
      if (existingAppkit) {
        existingAppkit.href = appkitHref;
      } else {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.type = 'text/css';
        link.id = 'appkit';
        link.href = appkitHref;
        document.head.appendChild(link);
      }
    } else if (existingAppkit) {
      existingAppkit.remove();
    }
  }

  sanitize(input: any): string {
    if (input && input.length && typeof input === 'string') {
      return input
        .replace(/<script[^>]?>.?<\/script>/gi, '')
        .replace(/<[\/\!]?[^<>]?>/gi, '')
        .replace(/<style[^>]?>.?<\/style>/gi, '')
        .replace(/<![\s\S]?--[ \t\n\r]>/gi, '');
    }
    return input;
  }
}
